import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, refreshCsrf } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './AuthPages.module.css';

export function SetupTotpPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const setup = useQuery({
    queryKey: ['2fa-setup', user?.id],
    queryFn: () =>
      apiFetch<{ qrDataUrl: string; secret: string }>('/api/auth/2fa/setup', { method: 'POST' }),
    enabled: !!user && !user.totpEnabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (user?.totpEnabled) navigate('/app/dashboard');
  }, [user, navigate]);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await refreshCsrf();
      const res = await apiFetch<{ recoveryCodes: string[] }>('/api/auth/2fa/confirm', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      setRecoveryCodes(res.recoveryCodes);
      await refresh();
    } catch {
      setError('Code ongeldig. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  }

  if (!user) return <LoadingState label="Sessie controleren…" />;
  if (setup.isLoading) return <LoadingState label="2FA voorbereiden…" />;
  if (setup.isError) return <ErrorState message="Kon 2FA-setup niet starten. Log opnieuw in." />;

  if (recoveryCodes) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.heading}>Bewaar je herstelcodes</h1>
          <p className={styles.sub}>Elke code is eenmalig te gebruiken. Bewaar ze offline.</p>
          <div className={styles.codes}>{recoveryCodes.join('  ·  ')}</div>
          <Button fullWidth onClick={() => navigate('/app/dashboard')}>
            Naar dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={(e) => void confirm(e)}>
        <h1 className={styles.heading}>2FA instellen</h1>
        <p className={styles.sub}>Scan de QR-code met je authenticator-app.</p>
        {setup.data?.qrDataUrl ? (
          <img
            src={setup.data.qrDataUrl}
            alt="QR-code voor authenticator"
            width={200}
            height={200}
          />
        ) : null}
        <Field label="Verificatiecode" error={error}>
          <TextInput
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            required
            value={token}
            onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </Field>
        <Button type="submit" fullWidth disabled={loading}>
          Activeer 2FA
        </Button>
      </form>
    </div>
  );
}
